import React, { useLayoutEffect, useRef, useState } from "react";
import Figure from "./Figure";

// One place on the shield. It is a BUTTON, because it does something: it
// selects the figure and puts what the printing says about it into the panel
// below. A div with a click handler would be unreachable by keyboard, and the
// hover card would then be the only way to the information — which is the trap
// every tooltip falls into.
//
// THE CARD IS A REAL TOOLTIP, so it implements one: it opens on pointer AND on
// focus, the button points at it with `aria-describedby` while it is open, and
// Escape dismisses it without moving focus. The house rule is that an ARIA
// pattern you announce is one you implement.
//
// It is also never the only copy of anything. Everything on the card is in the
// detail panel too, in full and with its locator; resting on a figure is a
// shortcut, not the only door.
//
// STATE IS NEVER FILL ALONE. A dimmed figure is one the walk has not reached,
// the current one is ringed, and the SELECTED one carries the house's 2px
// underline — `--ground-lift` against `--ground` is 1.05:1 and reads as
// nothing at all.
export default function Cell({
  cell,
  figure,
  size = 44,
  state = null,
  selected = false,
  arriving = false,
  revealed = null,
  landingLine = null,
  sourceLine = null,
  beat = 0,
  showCard = false,
  onSelect,
  onHover
}) {
  const cellRef = useRef(null);
  const cardRef = useRef(null);
  const [cardSide, setCardSide] = useState("below");
  const [cardAlign, setCardAlign] = useState("center");
  const classes = ["shield__cell"];
  if (cell.cast) classes.push("shield__cell--cast");
  if (cell.key === "judge") classes.push("shield__cell--judge");
  if (state) classes.push(`shield__cell--${state}`);
  if (arriving) classes.push("shield__cell--arriving");
  if (selected) classes.push("shield__cell--selected");
  // A figure being READ to make another one is lit too, not only the one being
  // made: an assembly you can only see one end of is not an assembly.
  if (sourceLine !== null) classes.push("shield__cell--reading");

  const cardId = `geo-card-${cell.key}`;
  const place = cell.house ? `${cell.role} · House ${cell.house.number}` : cell.role;

  // A full three-part reading is taller than the old one-sentence tooltip.
  // Choose the side with enough room when it opens; the card remains anchored
  // to its own cell, and the phone layout uses its fixed viewport treatment.
  useLayoutEffect(() => {
    if (!showCard) {
      setCardSide("below");
      setCardAlign("center");
      return;
    }
    if (!cellRef.current || !cardRef.current) return;

    const cellBox = cellRef.current.getBoundingClientRect();
    const cardBox = cardRef.current.getBoundingClientRect();
    const below = window.innerHeight - cellBox.bottom - 12;
    const above = cellBox.top - 12;
    if (below < cardBox.height && above < cardBox.height) setCardSide("viewport");
    else setCardSide(below >= cardBox.height ? "below" : "above");
    if (cardBox.left < 8) setCardAlign("left");
    else if (cardBox.right > window.innerWidth - 8) setCardAlign("right");
    else setCardAlign("center");
  }, [showCard]);

  return (
    <li
      ref={cellRef}
      className={classes.join(" ")}
      data-testid={`shield-${cell.key}`}
      data-cell={cell.key}
      data-arrives={cell.step}
    >
      <button
        type="button"
        className="shield__pick"
        aria-pressed={selected}
        aria-describedby={showCard ? cardId : undefined}
        onClick={() => onSelect(cell.key)}
        onMouseEnter={() => onHover(cell.key)}
        onMouseLeave={() => onHover(null)}
        onFocus={() => onHover(cell.key)}
        onBlur={() => onHover(null)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && showCard) {
            event.stopPropagation();
            onHover(null);
          }
        }}
      >
        <Figure
          figure={figure}
          size={size}
          revealed={revealed}
          landingLine={landingLine}
          sourceLine={sourceLine}
          beat={beat}
        />
        <span className="shield__name">{figure.printedName}</span>
        <span className="shield__role">{place}</span>
        {cell.note ? <span className="shield__role shield__role--note">{cell.note}</span> : null}
      </button>

      {showCard ? (
        <div
          ref={cardRef}
          className={`hovercard geo-card geo-card--${cardSide} geo-card--align-${cardAlign}`}
          id={cardId}
          role="tooltip"
          data-testid="geo-hover-card"
        >
          <p className="hovercard__name">{figure.printedName}</p>
          <p className="hovercard__kind">
            {place} · {figure.dots} dots, {figure.even ? "even" : "odd"}
            {figure.planet ? ` · ${figure.planet}` : ""}
          </p>
          <div className="geo-card__meanings">
            <section className="geo-card__meaning" data-testid="geo-card-position">
              <h4>Meaning of the position</h4>
              {cell.house ? (
                <>
                  <p className="geo-card__subject">
                    House {cell.house.number} · {cell.house.name}
                  </p>
                  <p>{cell.house.topic?.quote || "No separate description is given for this house."}</p>
                </>
              ) : (
                <p>{structuralMeaning(cell)}</p>
              )}
            </section>

            <section className="geo-card__meaning" data-testid="geo-card-figure">
              <h4>Meaning of the figure</h4>
              {figure.nature ? (
                <>
                  <p>“{figure.nature.quote}”</p>
                  {figure.nature.note ? <p className="geo-card__note">{figure.nature.note}</p> : null}
                </>
              ) : (
                <p>No general description is given for this figure.</p>
              )}
            </section>

            <section className="geo-card__meaning geo-card__meaning--combined" data-testid="geo-card-combined">
              <h4>Meaning of the figure in this position</h4>
              {cell.house ? (
                <>
                  <p className="geo-card__subject">
                    {figure.printedName} in House {cell.house.number}
                  </p>
                  <p>“{cell.house.judgment.quote}”</p>
                  {cell.house.judgment.note ? <p className="geo-card__note">{cell.house.judgment.note}</p> : null}
                </>
              ) : (
                <p>
                  This position has no separate house judgment. The figure is read as the {cell.role.toLowerCase()}
                  {cell.key === "judge" ? " that concludes the shield." : " contributing to the final judgment."}
                </p>
              )}
            </section>
          </div>
          <p className="hovercard__attested">Choose it for its sources, derivation, and full reading.</p>
        </div>
      ) : null}
    </li>
  );
}

function structuralMeaning(cell) {
  if (cell.key === "judge") {
    return "The Judge is the final figure, made from the two Witnesses. It must contain an even number of dots.";
  }

  return `${cell.role} is a Witness: a derived figure made from two Nieces and combined with the other Witness to form the Judge. It is not one of the twelve houses.`;
}
